import { ACTION_CONTROL, ROUTE_KEY } from '../src/constants/action-permission';

/**
 * 动态路由文件
 * 路由配置参数说明（本文件所有路由项均遵循以下字段约定）：
 *
 * - path: 路由路径，支持动态参数（如 ":id"），用于 URL 匹配。
 *   - 示例：/member/detail/:id
 *   - 详情/编辑页通常与 hideInMenu: true 配合，仅通过跳转访问。
 *
 * - name: 菜单/页面显示名称。
 *
 * - icon: 菜单图标，Ant Design 的图标名称（仅在一级菜单生效，可选）。
 *
 * - component: 对应页面组件路径（以 src/pages 为根目录的相对路径，如 './Member/Dashboard'）。
 *
 * - routes: 子路由数组，支持多级嵌套路由。
 *
 * - ⚠️key: 路由唯一权限标识，使用 ROUTE_KEY 枚举。必须保证全局唯一，
 *   新增路由务必补充对应的 ROUTE_KEY，供角色/权限体系使用。
 *
 * - ⚠️access: 路由访问控制函数名（定义于 src/access.ts）。
 *   返回 boolean，用于控制当前用户是否具有访问/可见权限。
 *   - 例如：'canAccessPage'（通用页面级控制）、'canAccessAnalysis'（业务自定义）。
 *
 * - hideInMenu: 是否在侧边菜单隐藏（true 则不在菜单展示，但可直接访问）。
 *
 * - ⚠️hideInRoleManagement: 是否在角色权限配置页面中隐藏该路由项。
 *
 * - ⚠️actionPermissions: 页面内操作级权限清单（用于按钮/操作粒度的控制）。
 *   仅用于前端展示与控制，实际鉴权需配合后端。
 *   结构如下：
 *     [{
 *       label: string; // 操作名称（展示用）
 *       value: ACTION_CONTROL; // 操作权限枚举值（与权限系统对齐）
 *     }]
 *
 * 使用建议：
 * - 新增页面时：务必补充唯一 key（ROUTE_KEY），并根据需要配置 access 与 actionPermissions。
 * - 详情/编辑类页面：通常设置 hideInMenu: true
 */

const dynamicRoutes = [
  {
    path: '/analysis',
    name: '经营分析',
    icon: 'lineChart',
    key: ROUTE_KEY.Analysis,
    access: 'canAccessAnalysis',
    routes: [
      {
        path: '/analysis/income',
        name: '业绩分析',
        component: './OperationsAnalysis/Income',
        key: ROUTE_KEY.AnalysisIncome,
        access: 'canAccessPage',
      },
      {
        path: '/analysis/leads',
        name: '线索分析',
        component: './OperationsAnalysis/Leads',
        key: ROUTE_KEY.AnalysisLeads,
        access: 'canAccessPage',
      },
      {
        path: '/analysis/renewal',
        name: '续卡分析',
        component: './OperationsAnalysis/Renewal',
        key: ROUTE_KEY.AnalysisRenewal,
        access: 'canAccessPage',
      },
      {
        path: '/analysis/course-analysis',
        name: '课程分析',
        component: './OperationsAnalysis/CourseAnalysis',
        key: ROUTE_KEY.AnalysisCourseAnalysis,
        access: 'canAccessPage',
      },
      {
        path: '/analysis/class-analysis',
        name: '班级分析',
        component: './OperationsAnalysis/ClassAnalysis',
        key: ROUTE_KEY.AnalysisClassAnalysis,
        access: 'canAccessPage',
      },
      {
        path: '/analysis/consume',
        name: '消课分析',
        component: './OperationsAnalysis/Consume',
        key: ROUTE_KEY.AnalysisConsume,
        access: 'canAccessPage',
      },
      // {
      //   path: '/analysis/staff-performance-statistics',
      //   name: '(旧)员工业绩分析',
      //   component: './OperationsAnalysis/StaffPerformanceStatistics',
      //   key: ROUTE_KEY.AnalysisStaffPerformanceStatistics,
      //   access: 'canAccessPage',
      // },
      {
        path: '/analysis/performance-statistics',
        name: '员工业绩统计',
        component: './OperationsAnalysis/PerformanceStatistics',
        key: ROUTE_KEY.AnalysisPerformanceStatistics,
        access: 'canAccessPage',
      },
      {
        path: '/analysis/performance-statistics/detail',
        name: '业绩详情',
        component: './OperationsAnalysis/PerformanceStatistics/Detail',
        hideInMenu: true,
        key: ROUTE_KEY.AnalysisPerformanceStatisticsDetail,
        access: 'canAccessPage',
        actionPermissions: [{ label: '业绩修改', value: ACTION_CONTROL.EditPerformance }],
      },
      {
        path: '/analysis/business-export',
        name: '经营数据导出',
        component: './OperationsAnalysis/BusinessDataExport',
        key: ROUTE_KEY.AnalysisBusinessDataExport,
        access: 'canAccessPage',
      },
    ],
  },
  {
    path: '/business-assistant',
    name: '经营助手',
    icon: 'robot',
    key: ROUTE_KEY.BusinessAssistant,
    access: 'canAccessBusinessAssistant',
    hideInRoleManagement: true,
    routes: [
      {
        path: '/business-assistant/operating-report',
        name: '经营分析报告',
        component: './BusinessAssistant/OperatingReport',
        key: ROUTE_KEY.BusinessAssistantOperatingReport,
        access: 'canAccessOperatingReport',
      },
      {
        path: '/business-assistant/operating-report/detail/:id',
        name: '经营分析报告详情',
        component: './BusinessAssistant/OperatingReport/Detail',
        key: ROUTE_KEY.BusinessAssistantOperatingReportDetail,
        access: 'canAccessOperatingReport',
        hideInMenu: true,
      },
      {
        path: '/business-assistant/card-assistant',
        name: '续卡助手',
        component: './BusinessAssistant/CardAssistant',
        key: ROUTE_KEY.BusinessAssistantCardAssistant,
        access: 'canAccessBusinessAssistant',
      },
    ],
  },
  {
    path: '/clue-center',
    name: '线索中心',
    icon: 'ShareAlt',
    key: ROUTE_KEY.ClueCenter,
    access: 'canAccessPage',
    routes: [
      {
        path: '/clue-center/clue-pending',
        name: '未分配线索',
        component: './ClueCenter/CluePending',
        key: ROUTE_KEY.ClueCenterCluePending,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '线索录入', value: ACTION_CONTROL.ClueEntry },
          { label: '线索分配', value: ACTION_CONTROL.AssignClue },
        ],
      },
      {
        path: '/clue-center/clue-done',
        name: '已分配线索',
        component: './ClueCenter/ClueDone',
        key: ROUTE_KEY.ClueCenterClueDone,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '线索录入', value: ACTION_CONTROL.ClueDoneEntry },
          { label: '线索分配', value: ACTION_CONTROL.AssignDoneClue },
        ],
      },
      {
        path: '/clue-center/clue-done/:id',
        name: '线索详情',
        component: './ClueCenter/ClueDone/Detail',
        hideInMenu: true,
        key: ROUTE_KEY.ClueCenterClueDoneId,
        access: 'canAccessPage',
        // 功能操作权限
        actionPermissions: [
          { label: '编辑线索、修改关联用户', value: ACTION_CONTROL.EditClue },
          { label: '合并线索', value: ACTION_CONTROL.MergeClue },
        ],
      },
      {
        path: '/clue-center/clue-auto',
        name: '线索自动分配',
        component: './ClueCenter/ClueAuto',
        key: ROUTE_KEY.ClueCenterClueAuto,
        access: 'canAccessPage',
      },
    ],
  },
  {
    path: '/member',
    name: '会员中心',
    icon: 'crown',
    key: ROUTE_KEY.Member,
    access: 'canAccessPage',
    routes: [
      {
        path: '/member/dashboard',
        name: '会员看板',
        component: './Member/Dashboard',
        key: ROUTE_KEY.MemberDashboard,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '导入会员数据', value: ACTION_CONTROL.MemberDataImport },
          { label: '批量延期', value: ACTION_CONTROL.MemberCardBatchExtend },
        ],
      },
      {
        path: '/member/member-settings',
        name: '会员设置',
        component: './Member/MemberSettings',
        key: ROUTE_KEY.MemberSettings,
        access: 'canAccessPage',
      },
      {
        path: '/member/detail/:id',
        name: '用户详情',
        component: './Member/MemberDetail',
        hideInMenu: true,
        key: ROUTE_KEY.MemberDetailId,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '修改负责人', value: ACTION_CONTROL.ModifyOwner },
          { label: '编辑会员信息、更换登录手机号', value: ACTION_CONTROL.EditMemberInfo },
          { label: '在线支付开卡', value: ACTION_CONTROL.CreateOrder },
          { label: '线下收款开卡', value: ACTION_CONTROL.QuickOpenCard },
          // { label: '发放/失效课次券', value: ACTION_CONTROL.InvalidateClassSessionVoucher },
          {
            label: '停卡、转卡、修改开卡负责人',
            value: ACTION_CONTROL.ManageMemberCard,
          },
          {
            label: '修改会员卡有效期',
            value: ACTION_CONTROL.ModifyMemberCardValidity,
          },
          { label: '作废、退卡退款', value: ACTION_CONTROL.VoidRefundMemberCard },
        ],
      },
      {
        path: '/member/card-management',
        name: '卡种管理',
        component: './Member/CardManagement',
        key: ROUTE_KEY.MemberCardManagement,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '会员卡新建、编辑、上下线', value: ACTION_CONTROL.EditMemberCard },
          { label: '电子合同新建、编辑、删除', value: ACTION_CONTROL.EditContract },
        ],
      },
      {
        path: '/member/contract-preview',
        name: '合同预览',
        component: './Member/ContractList/ContractPreview',
        hideInMenu: true,
        key: ROUTE_KEY.MemberContractPreview,
        hideInRoleManagement: true,
        access: 'canAccessPage',
      },
      {
        path: '/member/open-card-record',
        name: '全部会员卡',
        component: './Member/OpenCardRecord',
        key: ROUTE_KEY.MemberOpenCardRecord,
        access: 'canAccessPage',
      },
      {
        path: '/member/feedback',
        name: '会员反馈',
        component: './Member/Feedback',
        key: ROUTE_KEY.MemberFeedback,
        access: 'canAccessPage',
      },
    ],
  },
  {
    path: '/course-management',
    name: '课程管理',
    icon: 'bank',
    key: ROUTE_KEY.CourseManagement,
    access: 'canAccessPage',
    routes: [
      {
        path: '/course-management/schedule',
        name: '课表排课',
        component: './CourseManagement/Schedule',
        key: ROUTE_KEY.CourseManagementSchedule,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '新建排课、复制课表、发布课程', value: ACTION_CONTROL.Schedule },
        ],
      },
      {
        path: '/course-management/course-detail/:id',
        name: '课程详情',
        component: './CourseManagement/CourseDetail',
        hideInMenu: true,
        key: ROUTE_KEY.CourseManagementCourseDetailId,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '编辑课程、编辑介绍', value: ACTION_CONTROL.EditCourse },
          { label: '取消课程', value: ACTION_CONTROL.CancelCourse },
          { label: '课时费结算', value: ACTION_CONTROL.CourseSettlement },
          { label: '代约课', value: ACTION_CONTROL.DelegateCourse },
          { label: '取消用户预约、代签到', value: ACTION_CONTROL.CancelUserAppointment },
          { label: '上传课堂视频', value: ACTION_CONTROL.UploadClassVideo },
          { label: '调整扣减课次', value: ACTION_CONTROL.AdjustPayNum },
          // { label: '批量发券', value: ACTION_CONTROL.BatchSendCoupon },
          { label: '班级课程 - 考勤', value: ACTION_CONTROL.ClassCourseAttendance },
        ],
      },
      {
        path: '/course-management/cancelled-course',
        name: '已取消排课',
        component: './CourseManagement/CancelledCourse',
        key: ROUTE_KEY.CourseManagementCancelledCourse,
        access: 'canAccessPage',
      },
      {
        path: '/course-management/course-settings',
        name: '课程设置',
        component: './CourseManagement/CourseSettings',
        key: ROUTE_KEY.CourseManagementCourseSettings,
        access: 'canAccessPage',
      },
      {
        path: '/course-management/course-type',
        name: '课程类型设置',
        component: './CourseManagement/CourseType',
        key: ROUTE_KEY.CourseManagementCourseType,
        access: 'canAccessPage',
      },
      {
        path: '/course-management/dance-style',
        name: '舞种设置',
        component: './CourseManagement/DanceStyle',
        key: ROUTE_KEY.CourseManagementDanceStyle,
        access: 'canAccessPage',
      },
    ],
  },
  {
    path: '/online-course',
    name: '线上课',
    icon: 'read',
    key: ROUTE_KEY.OnlineCourse,
    access: 'canAccessPage',
    routes: [
      {
        path: '/online-course/course-list',
        name: '平台课程',
        component: './OnlineCourse/CourseList',
        hideInRoleManagement: true,
        key: ROUTE_KEY.OnlineCourseCourseList,
      },
      {
        path: '/online-course/course-detail/:id',
        name: '课程详情',
        component: './OnlineCourse/CourseDetail',
        hideInMenu: true,
        key: ROUTE_KEY.OnlineCourseCourseDetailId,
        actionPermissions: [
          { label: '设置会员卡价格', value: ACTION_CONTROL.OnlineCourseSetPrice },
          { label: '代约课', value: ACTION_CONTROL.OnlineCourseDelegate },
          { label: '调整扣减课次', value: ACTION_CONTROL.OnlineCourseAdjustPayNum },
        ],
      },
      {
        name: '现金优惠券',
        path: '/online-course/coupon-list',
        component: './OnlineCourse/CouponList',
        key: ROUTE_KEY.OnlineCourseCouponList,
        access: 'canAccessPage',
        actionPermissions: [{ label: '优惠券发放、失效', value: ACTION_CONTROL.OperateCoupon }],
      },
      {
        name: '优惠券详情',
        path: '/online-course/coupon-detail/:id',
        component: './OnlineCourse/CouponList/Detail',
        hideInRoleManagement: true,
        hideInMenu: true,
        key: ROUTE_KEY.OnlineCourseCouponDetailId,
      },
      {
        path: '/online-course/online-course-income',
        name: '线上课收入',
        component: './OnlineCourse/OnlineCourseIncome',
        key: ROUTE_KEY.OnlineCourseOnlineCourseIncome,
        access: 'canAccessPage',
      },
      {
        path: '/online-course/online-course-income-detail/:id',
        name: '线上课收入详情',
        component: './OnlineCourse/OnlineCourseIncome/detail',
        hideInRoleManagement: true,
        hideInMenu: true,
        key: ROUTE_KEY.OnlineCourseOnlineCourseIncomeDetailId,
      },
    ],
  },
  {
    path: '/studio-management',
    name: '门店管理',
    icon: 'shop',
    key: ROUTE_KEY.StudioManagement,
    access: 'canAccessPage',
    routes: [
      {
        path: '/studio-management/studio-list',
        name: '门店列表',
        component: './StudioManagement/Studio',
        key: ROUTE_KEY.StudioManagementStudioList,
        access: 'canAccessPage',
      },
      {
        path: '/studio-management/studio-detail/:id',
        name: '门店详情',
        component: './StudioManagement/Studio/Detail',
        hideInRoleManagement: true,
        hideInMenu: true,
        key: ROUTE_KEY.StudioManagementStudioDetailId,
      },
      {
        path: '/studio-management/staff-list',
        name: '员工列表',
        component: './StudioManagement/Staff',
        key: ROUTE_KEY.StudioManagementStaffList,
        access: 'canAccessPage',
      },
    ],
  },
  {
    path: '/class-management',
    name: '班级管理',
    icon: 'team',
    key: ROUTE_KEY.ClassManagement,
    access: 'canAccessPage',
    routes: [
      {
        path: '/class-management/class-list',
        name: '班级管理',
        component: './ClassManagement/ClassList',
        key: ROUTE_KEY.ClassManagementClassList,
        access: 'canAccessPage',
        actionPermissions: [{ label: '新建、编辑班级', value: ACTION_CONTROL.CreateClass }],
      },
      {
        path: '/class-management/class-detail/:id',
        name: '班级详情',
        component: './ClassManagement/ClassDetail',
        hideInMenu: true,
        key: ROUTE_KEY.ClassManagementClassDetailId,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '学员管理（添加、移出、换班）', value: ACTION_CONTROL.ManageStudent },
          { label: '班级排课', value: ACTION_CONTROL.ScheduleClass },
          { label: '班级结课、删除班级', value: ACTION_CONTROL.ClassEnd },
        ],
      },
    ],
  },
  {
    path: '/teacher-management',
    name: '师资管理',
    icon: 'audit',
    key: ROUTE_KEY.TeacherManagement,
    access: 'canAccessPage',
    routes: [
      {
        path: '/teacher-management/teacher-list',
        name: '老师列表',
        component: './TeacherManagement/TeacherList',
        key: ROUTE_KEY.TeacherManagementTeacherList,
        access: 'canAccessPage',
        actionPermissions: [{ label: '课时费设置', value: ACTION_CONTROL.TeacherSetReceiveLimit }],
      },
      {
        path: '/teacher-management/teacher-detail/:id',
        name: '老师详情',
        component: './TeacherManagement/TeacherDetail',
        hideInRoleManagement: true,
        hideInMenu: true,
        key: ROUTE_KEY.TeacherManagementTeacherDetailId,
      },
      {
        path: '/teacher-management/teacher-settlement-class-time',
        name: '课时费结算',
        component: './TeacherManagement/TeacherSettlementClassTime',
        key: ROUTE_KEY.TeacherManagementTeacherSettlementClassTime,
        access: 'canAccessPage',
      },
    ],
  },
  {
    path: '/marketing-center',
    name: '营销中心',
    icon: 'fire',
    key: ROUTE_KEY.MarketingCenter,
    access: 'canAccessPage',
    badge: 'New',
    routes: [
      {
        path: '/marketing-center/marketing-list',
        name: '课次券',
        component: './MarketingCenter/ClassSessionVoucher',
        key: ROUTE_KEY.MarketingCenterMarketingList,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '课次券新建、编辑、下线', value: ACTION_CONTROL.EditClassSessionVoucher },
          { label: '课次券发放、失效', value: ACTION_CONTROL.InvalidateClassSessionVoucher },
        ],
      },
      {
        path: '/marketing-center/member-card-cash-coupon',
        name: '会员卡代金券',
        component: './MarketingCenter/MemberCardCashCoupon',
        key: ROUTE_KEY.MarketingCenterMemberCardCashCoupon,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '代金券的新建、编辑、下线', value: ACTION_CONTROL.EditMemberCardCashCoupon },
          { label: '代金券的发放、失效', value: ACTION_CONTROL.OperateMemberCardCashCoupon },
        ],
      },
      {
        path: '/marketing-center/marketing-detail/:id',
        name: '课次券详情',
        component: './MarketingCenter/ClassSessionVoucher/Detail',
        hideInRoleManagement: true,
        hideInMenu: true,
        key: ROUTE_KEY.MarketingCenterMarketingDetailId,
      },
      {
        path: '/marketing-center/user-data-report',
        name: '数据报告',
        component: './MarketingCenter/UserDataReport',
        key: ROUTE_KEY.MarketingCenterUserDataReport,
        access: 'canAccessPage',
      },
      {
        path: '/marketing-center/beginner-plan',
        name: '新手计划',
        component: './MarketingCenter/BeginnerPlan',
        key: ROUTE_KEY.MarketingCenterBeginnerPlan,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '新手计划新建、编辑、上下线', value: ACTION_CONTROL.EditBeginnerPlan },
        ],
      },
      {
        path: '/marketing-center/beginner-plan/edit',
        name: '新手计划编辑',
        component: './MarketingCenter/BeginnerPlan/Edit',
        key: ROUTE_KEY.MarketingCenterBeginnerPlanEdit,
        hideInMenu: true,
        hideInRoleManagement: true,
      },
      {
        path: '/marketing-center/beginner-plan/user-data',
        name: '新手计划用户数据',
        component: './MarketingCenter/BeginnerPlan/UserData',
        key: ROUTE_KEY.MarketingCenterBeginnerPlanUserData,
        hideInMenu: true,
        hideInRoleManagement: true,
      },
      {
        path: '/marketing-center/referral/data',
        name: '转介绍数据',
        component: './MarketingCenter/Referral/Data',
        key: ROUTE_KEY.MarketingCenterReferralData,
        hideInMenu: true,
        hideInRoleManagement: true,
      },
      {
        path: '/marketing-center/referral',
        name: '转介绍',
        component: './MarketingCenter/Referral',
        key: ROUTE_KEY.MarketingCenterReferral,
        access: 'canAccessPage',
        actionPermissions: [
          { label: '转介绍新建、编辑、上下线', value: ACTION_CONTROL.EditReferral },
        ],
      },
      {
        path: '/marketing-center/bisaiyi-event',
        name: '赛事活动',
        component: './MarketingCenter/BisaiyiEvent',
        hideInRoleManagement: true,
        key: ROUTE_KEY.MarketingCenterBisaiyiEvent,
        access: 'canAccessPage',
      },
      {
        path: '/marketing-center/ai-poster',
        name: '海报设计',
        component: './MarketingCenter/AiPoster',
        key: ROUTE_KEY.MarketingCenterAiPoster,
        badge: '限免',
        hideInRoleManagement: true,
      },
      {
        path: '/marketing-center/dancer-id-card',
        name: '舞者成就卡',
        component: './MarketingCenter/DancerIdCard',
        key: ROUTE_KEY.MarketingCenterDancerIdCard,
        access: 'canAccessPage',
        actionPermissions: [
          {
            label: '舞者成就卡开通、购买、修改规则、关闭',
            value: ACTION_CONTROL.ManageDancerIdCard,
          },
        ],
      },
    ],
  },
  {
    path: '/finance-management',
    name: '财务管理',
    icon: 'MoneyCollect',
    key: ROUTE_KEY.FinanceManagement,
    access: 'canAccessPage',
    routes: [
      {
        path: '/finance-management/cash-flow-income-list',
        name: '门店现金流收入',
        component: './FinanceManagement/CashFlowIncomeList',
        key: ROUTE_KEY.FinanceManagementCashFlowIncomeList,
        access: 'canAccessPage',
      },
      {
        path: '/finance-management/cash-flow-income-detail/:id',
        name: '现金流收入详情',
        component: './FinanceManagement/CashFlowIncomeDetail',
        hideInRoleManagement: true,
        hideInMenu: true,
        key: ROUTE_KEY.FinanceManagementCashFlowIncomeDetailId,
        access: 'canAccessPage',
      },
      {
        path: '/finance-management/confirm-income-list',
        name: '门店确认收入',
        component: './FinanceManagement/ConfirmIncomeList',
        key: ROUTE_KEY.FinanceManagementConfirmIncomeList,
        access: 'canAccessPage',
      },
      {
        path: '/finance-management/confirm-income-detail/:id',
        name: '确认收入详情',
        component: './FinanceManagement/ConfirmIncomeDetail',
        hideInRoleManagement: true,
        hideInMenu: true,
        key: ROUTE_KEY.FinanceManagementConfirmIncomeDetailId,
      },
      {
        path: '/finance-management/payment-account',
        name: '收款账户',
        component: './FinanceManagement/PaymentAccount',
        key: ROUTE_KEY.FinanceManagementPaymentAccount,
        access: 'canAccessPage',
      },
      {
        path: '/finance-management/payment-account/auth/:id',
        name: '签约认证',
        component: './FinanceManagement/PaymentAccount/Auth',
        hideInRoleManagement: true,
        hideInMenu: true,
        key: ROUTE_KEY.FinanceManagementPaymentAccountAuthId,
      },
      {
        path: '/finance-management/withdraw',
        name: '提现记录',
        component: './FinanceManagement/Withdraw',
        key: ROUTE_KEY.FinanceManagementWithdraw,
        access: 'canAccessPage',
      },
      {
        path: '/finance-management/order-list',
        name: '线上支付订单',
        component: './FinanceManagement/OrderList',
        key: ROUTE_KEY.FinanceManagementOrderList,
        access: 'canAccessPage',
      },
      {
        path: '/finance-management/order-detail/:id',
        name: '订单详情',
        component: './FinanceManagement/OrderDetail',
        hideInRoleManagement: true,
        hideInMenu: true,
        key: ROUTE_KEY.FinanceManagementOrderDetailId,
      },
    ],
  },
  {
    path: '/merchant-settings',
    name: '商户设置',
    icon: 'setting',
    key: ROUTE_KEY.MerchantSettings,
    access: 'canAccessPage',
    routes: [
      {
        path: '/merchant-settings/basic-settings',
        name: '商户基础信息',
        component: './MerchantSettings/BasicSettings',
        key: ROUTE_KEY.MerchantSettingsBasicSettings,
        access: 'canAccessPage',
      },
      {
        path: '/merchant-settings/mini-program',
        name: '微信小程序设置',
        component: './MerchantSettings/MiniProgram',
        key: ROUTE_KEY.MerchantSettingsMiniProgram,
        access: 'canAccessPage',
      },
      {
        path: '/merchant-settings/video-storage',
        name: '视频存储',
        component: './MerchantSettings/VideoStorage',
        hideInMenu: true,
        hideInRoleManagement: true,
        key: ROUTE_KEY.MerchantSettingsVideoStorage,
      },
      {
        path: '/merchant-settings/service-notifications',
        name: '服务通知',
        component: './MerchantSettings/ServiceNotifications',
        key: ROUTE_KEY.MerchantSettingsServiceNotifications,
        access: 'canAccessPage',
      },
    ],
  },
];

export default dynamicRoutes;
